// Pure helpers shared across the Bundle Builder UI.

// Map internal range key to user-facing label (display name shown in section headers).
// Internal key = stored value in MongoDB; label = what the user sees on screen.
const RANGE_LABELS = {
  Core: 'Protein Core',
  Goal: 'Goal',
  Performance: 'Performance',
  Essentials: 'Essentials',
};
export const rangeLabel = (rangeKey) => RANGE_LABELS[rangeKey] || rangeKey;

// Core box size based on product size (null for non-Core or unknown sizes).
// Small-format tubs → box of 8 (small saver shape: 1 Core + 2 supporting).
// Large-format tubs → box of 6 (large saver shape: 1 Core + 3 supporting, ≥ 2 ranges).
const BOX_SIZE_BY_SIZE = {
  '1kg': 8,
  '900g': 8,
  '2kg': 6,
  '1.8kg': 6,
};
// Sizes that use the LARGE saver discount tier (large_single_pct / large_box_pct).
// Everything else uses the small tier.
export const LARGE_SAVER_SIZES = new Set(['2kg', '1.8kg']);
export const getFoundationBoxSize = (product) => {
  if (!product || product.range !== 'Core' || !product.size) return null;
  return BOX_SIZE_BY_SIZE[product.size] || null;
};

// Default wholesale discount when a customer has no per-product override.
// Single source of truth — backend mirrors this constant in server._customer_unit_price.
export const DEFAULT_WHOLESALE_DISCOUNT_PCT = 25;

// NZ GST rate: RRPs are stored GST-inclusive. Wholesale prices are ex-GST, so
// we strip GST from the RRP before applying the customer's discount. GST is
// added back at invoicing. This mirrors backend server.py:GST_INCLUSIVE_DIVISOR.
export const GST_INCLUSIVE_DIVISOR = 1.15;

// Money/percent precision rules (Jun 2026): unit prices are cent-true (2 dp) and
// discount percentages are capped at 1 dp so a customer manually reconciling their
// total from displayed prices and rates gets the exact same answer as the app.
export const roundCents = (v) => Math.round((Number(v) || 0) * 100) / 100;
export const roundPct1 = (v) => Math.round((Number(v) || 0) * 10) / 10;

// Canonical render order for protein-style flavours. Any flavour matching one of these
// names renders in this exact order; everything else (Lime Milkshake, Hokey Pokey,
// special-edition flavours, etc.) is appended after in its original input order.
const CANONICAL_FLAVOUR_ORDER = ['Vanilla', 'Chocolate', 'Strawberry', 'Banana'];

export const sortFlavours = (flavours = []) => {
  if (!Array.isArray(flavours) || flavours.length === 0) return [];
  const idx = (f) => {
    const i = CANONICAL_FLAVOUR_ORDER.indexOf(f);
    return i === -1 ? Number.POSITIVE_INFINITY : i;
  };
  // Stable sort: known flavours go first in canonical order, unknown stay in input order.
  return [...flavours].sort((a, b) => {
    const ai = idx(a);
    const bi = idx(b);
    if (ai === Number.POSITIVE_INFINITY && bi === Number.POSITIVE_INFINITY) {
      return flavours.indexOf(a) - flavours.indexOf(b);
    }
    return ai - bi;
  });
};

// Resolve the customer-specific unit price for one saved product line.
// Priority: stored `unitPrice` snapshot → fall back to `wholesalePrice`.
// `unitPrice` is set when the bundle is saved and reflects any per-product discount
// override active at that moment ((RRP / 1.15) × (1 - productDiscountPct/100)).
const lineUnitPrice = (p) => {
  if (typeof p?.unitPrice === 'number' && Number.isFinite(p.unitPrice)) return roundCents(p.unitPrice);
  return roundCents(Number(p?.wholesalePrice) || 0);
};

// Resolve the customer-specific unit price for a LIVE (un-saved) product line.
// `productDiscounts` maps `productId -> pct off the ex-GST RRP`. Returned value is
// **ex-GST** — GST is added back at invoicing. If no override is set, falls back
// to (RRP / 1.15) × (1 - DEFAULT_WHOLESALE_DISCOUNT_PCT/100). There is no longer
// a stored "base wholesale" on the product record.
export const customerUnitPrice = (product, productDiscounts = {}) => {
  if (!product) return 0;
  const pct = productDiscounts[product.id];
  const effectivePct = (typeof pct === 'number' && Number.isFinite(pct))
    ? pct
    : DEFAULT_WHOLESALE_DISCOUNT_PCT;
  const exGstRrp = Number(product.rrp) / GST_INCLUSIVE_DIVISOR;
  return roundCents(exGstRrp * (1 - effectivePct / 100));
};

// True when the price was derived from the fallback default discount (no per-product
// override set for this customer). UI uses this to show "(inquire for wholesale price)"
// on customer-facing surfaces and to optionally hide the numeric price.
export const isDefaultWholesalePrice = (product, productDiscounts = {}) => {
  if (!product) return true;
  const pct = productDiscounts[product.id];
  return !(typeof pct === 'number' && Number.isFinite(pct));
};

// Describe the source of a product's unit price for admin-facing tooltips.
// Formula: strip GST from RRP (÷ 1.15) then apply the customer's override %.
// Displayed value is ex-GST.
export const priceCalcDescription = (product, productDiscounts = {}) => {
  if (!product) return null;
  const pct = productDiscounts[product.id];
  const unit = customerUnitPrice(product, productDiscounts);
  const exGst = Number(product.rrp) / GST_INCLUSIVE_DIVISOR;
  if (typeof pct === 'number' && Number.isFinite(pct)) {
    return {
      kind: 'override',
      pct,
      unit,
      formula: `RRP $${Number(product.rrp).toFixed(2)} ÷ 1.15 = $${exGst.toFixed(2)} ex-GST × (1 − ${pct}%) = $${unit.toFixed(2)} ex-GST`,
      shortBadge: `RRP − GST − ${pct}%`,
    };
  }
  return {
    kind: 'default',
    pct: DEFAULT_WHOLESALE_DISCOUNT_PCT,
    unit,
    formula: `RRP $${Number(product.rrp).toFixed(2)} ÷ 1.15 = $${exGst.toFixed(2)} ex-GST × (1 − ${DEFAULT_WHOLESALE_DISCOUNT_PCT}%) = $${unit.toFixed(2)} ex-GST (default; no per-customer override)`,
    shortBadge: `RRP − GST − ${DEFAULT_WHOLESALE_DISCOUNT_PCT}% (default)`,
  };
};

// Compute aggregated totals for a saved bundle.
//
// Saver pricing rule (Feb 2026):
//   * Core products are added at the customer's unit price (RRP − per-product %, or
//     wholesalePrice fallback). NO saver discount is applied to Core lines.
//   * Supporting products (Goal / Performance / Essentials) receive the saver-tier
//     discount on top of their unit price.
//
//   coreSubtotal       = sum(Core unit prices)
//   supportingSubtotal = sum(non-Core unit prices)
//   bundlePrice        = coreSubtotal + supportingSubtotal × (1 − discountPct/100)
//   customerSaving     = supportingSubtotal × (discountPct/100)
//   subtotal           = coreSubtotal + supportingSubtotal (pre-discount reference)
export const computeBundleTotals = (bundle) => {
  let coreSubtotal = 0;
  let supportingSubtotal = 0;
  for (const p of bundle.products) {
    const unit = lineUnitPrice(p);
    if (p?.range === 'Core') coreSubtotal += unit;
    else supportingSubtotal += unit;
  }
  const subtotal = roundCents(coreSubtotal + supportingSubtotal);
  const discountPct = roundPct1(bundle.discountPct);
  const supportingDiscounted = roundCents(supportingSubtotal * (1 - discountPct / 100));
  const bundlePrice = roundCents(coreSubtotal + supportingDiscounted);
  const customerSaving = roundCents(supportingSubtotal - supportingDiscounted);
  const savingPercent = subtotal > 0 ? (customerSaving / subtotal) * 100 : 0;
  return {
    subtotal,
    coreSubtotal: roundCents(coreSubtotal),
    supportingSubtotal: roundCents(supportingSubtotal),
    bundlePrice,
    customerSaving,
    savingPercent,
  };
};

// Illustrative PRO75 margin-boost example: spread a saver saving equally across
// every PRO75 Core tub, then show the theoretical per-tub margin uplift (ex-GST).
// Base margin per tub = (RRP ÷ 1.15) − wholesale unit price. Returns null when
// there are no PRO75 tubs or no saving to spread. Pass `productDiscounts` for a
// LIVE selection; omit it for saved-bundle lines (uses the unitPrice snapshot).
export const computePro75MarginExample = (products, totalSaving, productDiscounts = null) => {
  const tubs = (products || []).filter(
    (p) => p?.range === 'Core' && (p?.name || '').trim().toUpperCase() === 'PRO75'
  );
  const saving = roundCents(totalSaving);
  if (tubs.length === 0 || saving <= 0) return null;
  const unitOf = (p) => (productDiscounts ? customerUnitPrice(p, productDiscounts) : lineUnitPrice(p));
  const perTubBoost = saving / tubs.length;
  const sizes = [];
  for (const tub of tubs) {
    if (sizes.some((s) => s.size === tub.size)) continue;
    const exGstRrp = Number(tub.rrp) / GST_INCLUSIVE_DIVISOR;
    const baseMargin = roundCents(exGstRrp - unitOf(tub));
    const boostedMargin = roundCents(baseMargin + perTubBoost);
    sizes.push({
      size: tub.size,
      baseMargin,
      boostedMargin,
      baseMarginPct: roundPct1(exGstRrp > 0 ? (baseMargin / exGstRrp) * 100 : 0),
      boostedMarginPct: roundPct1(exGstRrp > 0 ? (boostedMargin / exGstRrp) * 100 : 0),
    });
  }
  return { tubCount: tubs.length, totalSaving: saving, perTubBoost: roundCents(perTubBoost), sizes };
};

// Aggregate totals across a list of saved bundles for the whole order.
export const computeOrderTotals = (bundles) => {
  let subtotal = 0;
  let discountedTotal = 0;
  for (const b of bundles || []) {
    const t = computeBundleTotals(b);
    subtotal += t.subtotal;
    discountedTotal += t.bundlePrice;
  }
  const totalDiscount = subtotal - discountedTotal;
  const totalDiscountPct = subtotal > 0 ? (totalDiscount / subtotal) * 100 : 0;
  return { subtotal, discountedTotal, totalDiscount, totalDiscountPct };
};

// Group identical product+flavour selections into one row with a quantity (×N).
// Preserves the order of first appearance.
export const groupSelections = (items) => {
  const groups = new Map();
  for (const p of items) {
    const key = `${p.id}::${p.selectedFlavour || ''}`;
    const existing = groups.get(key);
    if (existing) {
      existing.qty += 1;
    } else {
      groups.set(key, {
        id: p.id,
        name: p.name,
        flavourCodes: p.flavourCodes || {},
        range: p.range || null,
        size: p.size || null,
        selectedFlavour: p.selectedFlavour || null,
        rrp: p.rrp,
        wholesalePrice: p.wholesalePrice,
        unitPrice: typeof p.unitPrice === 'number' ? p.unitPrice : undefined,
        qty: 1,
      });
    }
  }
  return Array.from(groups.values());
};

// Total quantity for Foundation Box mode: sum of selected flavour qtys.
export const totalFoundationBoxQty = (product, getFlavourQty) => {
  if (!product?.flavours || !getFlavourQty) return 0;
  return product.flavours.reduce((sum, f) => sum + getFlavourQty(product.id, f), 0);
};

// Decide the "× N" badge text for a product card (null = no badge).
// Replaces a deeply nested ternary in ProductCard.
export const cardQtyBadge = ({ boxMode, totalBoxQty, quantityMode, productQty, hasFlavours, multiFlavourMode, selectedCount, product, getFlavourQty }) => {
  if (boxMode && totalBoxQty > 0) return totalBoxQty;
  if (!boxMode && quantityMode && productQty > 0 && hasFlavours) {
    return product.flavours.reduce((s, f) => s + (getFlavourQty ? getFlavourQty(product.id, f) : 0), 0);
  }
  // No-flavour supporting card (single OR box mode): show productQty when > 0.
  if (!boxMode && !hasFlavours && productQty > 0) return productQty;
  if (!boxMode && !quantityMode && multiFlavourMode && selectedCount > 0) return selectedCount;
  return null;
};

// Resolve the effective product code for a line item: per-flavour code only (empty if none).
export const resolveProductCode = (item) => {
  if (!item) return '';
  const flavour = item.selectedFlavour;
  const codes = item.flavourCodes || {};
  return (flavour && codes[flavour]) ? codes[flavour].toString() : '';
};

// Per-flavour qty counter colour class (replaces 3-arm ternary).
export const flavourCounterTone = (currentTotal, target) => {
  if (currentTotal === target) return 'text-success';
  if (currentTotal > target) return 'text-danger';
  return 'text-warning';
};

// Decide if a supporting product's `+` button should be disabled because the per-range
// cap (N = Foundation count) would be exceeded. Mirrors the backend rule in
// `bundle_rules._check_per_range_cap` including the Pure Series Aminos exception.
const PURE_AMINOS = 'Pure Series Aminos';
export const isSupportingPlusDisabled = (product, selectedProducts) => {
  if (!product || product.range === 'Core') return false;
  const foundationCount = selectedProducts.filter((p) => p.range === 'Core').length;
  if (foundationCount === 0) return false;
  const rangeProducts = selectedProducts.filter((p) => p.range === product.range);
  const aminosInRange = rangeProducts.filter((p) => p.name === PURE_AMINOS).length;
  const effective = rangeProducts.length - Math.max(0, aminosInRange - 1);
  if (product.name === PURE_AMINOS) {
    // Subsequent Aminos collapse to the same slot; only the FIRST Aminos increments effective.
    return aminosInRange === 0 && effective >= foundationCount;
  }
  return effective >= foundationCount;
};

// TOTAL supporting cap across ALL categories: 1 supporting item per 1kg Core tub,
// 2 per 2kg tub. Returns null when no Core is selected yet (cap not applicable).
export const supportingCapFor = (selectedProducts, proteinSize) => {
  const cores = selectedProducts.filter((p) => p.range === 'Core').length;
  if (cores === 0) return null;
  return cores * (LARGE_SAVER_SIZES.has(proteinSize) ? 2 : 1);
};

// True once the order-wide supporting total has hit the cap — every add option
// (any category) must then be unavailable until something is removed.
export const isSupportingTotalCapReached = (selectedProducts, proteinSize) => {
  const cap = supportingCapFor(selectedProducts, proteinSize);
  if (cap === null) return false;
  return selectedProducts.filter((p) => p.range !== 'Core').length >= cap;
};
